import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useObserver } from 'mobx-react';
import { makeStyles, MuiThemeProvider } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import TableCell from '@material-ui/core/TableCell';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Link from '../Link';
import withStores from '../../stores/withStores';
import Table from '../Table';
import useWidth from '../../utils/useWidth';
import adaTheme from '../../theme';
import ConfirmationDialog from '../ConfirmationDialog';
import PageStateDialog from './PageStateDialog';
import useI18n from '../../i18n/useI18n';

import moment from 'moment';

const useStyles = makeStyles(theme => ({
  type: {
    textTransform: 'capitalize',
  },
  actionCell: {
    textAlign: 'right',
  },
  chipWrap: {
    display: 'flex',
    // justifyContent: 'center',
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(0, 0.5, 0.5, 0),
    },
  },
  date: {},
}));

const PageInteractionCell = props => {
  const { datum, onDeleteAction, onEditAction } = props;
  const classes = useStyles();
  return (
    <TableCell classes={{ root: classes.actionCell }}>
      <Box display="flex" justifyContent="flex-end">
        {null && (
          <Box color="text.secondary">
            <IconButton
              onClick={() => onEditAction(datum)}
              color="inherit"
              variant="contained"
            >
              <EditIcon />
            </IconButton>
          </Box>
        )}
        <Box color="text.disabled">
          <IconButton
            onClick={() => onDeleteAction(datum)}
            color="inherit"
            variant="contained"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>
    </TableCell>
  );
};

PageInteractionCell.propTypes = {
  datum: PropTypes.object.isRequired,
  onAction: PropTypes.func.isRequired,
};

const SelectPageActionCell = props => {
  const { datum, onAction } = props;
  const classes = useStyles();
  return (
    <TableCell classes={{ root: classes.actionCell }}>
      <Button
        color="primary"
        variant="contained"
        onClick={() => {
          onAction(datum);
        }}
      >
        Auswählen
      </Button>
    </TableCell>
  );
};

SelectPageActionCell.propTypes = {
  datum: PropTypes.object.isRequired,
  onAction: PropTypes.func.isRequired,
};

const getActionCell = (
  onSelectItem,
  { handleSetPageToDelete, handleSetPageToEdit },
) => {
  if (onSelectItem) {
    return props => <SelectPageActionCell {...props} onAction={onSelectItem} />;
  }
  return props => (
    <PageInteractionCell
      {...props}
      onDeleteAction={handleSetPageToDelete}
      onEditAction={handleSetPageToEdit}
    />
  );
};

const useHeaderAttributes = width => {
  const i18n = useI18n();
  switch (width) {
    case 'xs':
    case 'sm':
      return [
        { key: 'type', label: i18n.t('pages.attributes.type'), width: 120 },
        {
          key: 'title',
          label: i18n.t('pages.attributes.title'),
          width: 'auto',
        },
        {
          key: 'updatedAt',
          label: i18n.t('pages.attributes.updatedAt'),
          width: 190,
        },
        { key: 'state', label: i18n.t('pages.attributes.state'), width: 100 },
      ];
    default:
      return [
        { key: 'type', label: i18n.t('pages.attributes.type'), width: 120 },
        {
          key: 'title',
          label: i18n.t('pages.attributes.title'),
          width: 'auto',
        },
        {
          key: 'updatedAt',
          label: i18n.t('pages.attributes.updatedAt'),
          width: 190,
        },
        // { key: 'createdAt', label: 'Erstellt am', width: 190 },
        {
          key: 'createdByUserName',
          label: i18n.t('pages.attributes.createdByUserName'),
          width: 150,
        },
        { key: 'state', label: i18n.t('pages.attributes.state'), width: 100 },
      ];
  }
};

const PagesTable = props => {
  const { pagesStore, onSelectItem } = props;
  const classes = useStyles();
  const width = useWidth();
  const i18n = useI18n();
  const headerAttributes = useHeaderAttributes(width);
  const [pageToDelete, setPageToDelete] = React.useState(null);
  const [pageToEdit, setPageToEdit] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const handleSetPageToDelete = page => {
    setPageToDelete(page);
  };

  const handleSetPageToEdit = page => {
    setPageToEdit(page);
  };

  const handleCloseEditDialog = () => {
    setPageToEdit(null);
  };

  const handleCloseDialog = () => {
    setPageToDelete(null);
  };

  const handleConfirmDialog = async () => {
    await pagesStore.deletePage(pageToDelete.uuid);
    handleCloseDialog();
  };

  React.useEffect(() => {
    const loadPages = async () => {
      setIsLoading(true);
      await pagesStore.loadAllPages();
      setIsLoading(false);
    };
    loadPages();
  }, []);

  const ActionCell = getActionCell(onSelectItem, {
    handleSetPageToDelete,
    handleSetPageToEdit,
  });

  return useObserver(() => (
    <MuiThemeProvider theme={adaTheme}>
      <Table
        isLoading={isLoading}
        data={pagesStore.pages}
        headerAttributes={headerAttributes}
        queryAttributes={[
          'title',
          (p, query) =>
            p.tags.some(tag => tag && tag.toLowerCase().includes(query)),
        ]}
        defaultOrderBy="updatedAt"
        renderCell={(page, key) => {
          switch (key) {
            case 'title':
              return (
                <React.Fragment>
                  <Typography variant="body1" gutterBottom>
                    <Link hoverEffect href={`/admin/p/${page.uuid}`}>
                      {page.title || i18n.t('pages.state.noTitle')}
                    </Link>
                  </Typography>
                  <Box className={classes.chipWrap}>
                    {page.tags.map(t => (
                      <Chip key={t} size="small" label={t} color="secondary" />
                    ))}
                  </Box>
                </React.Fragment>
              );
            case 'type':
              return (
                <Typography className={classes.type} variant="body2">
                  {page[key]}
                </Typography>
              );
            case 'updatedAt':
            case 'createdAt':
              const date = moment(page[key]);
              return (
                <Tooltip title={`${date.format('LTS')}`} placement="right">
                  <Typography
                    className={cx(classes.type, classes.date)}
                    variant="body2"
                    component="span"
                  >
                    {date.format('LL')}
                  </Typography>
                </Tooltip>
              );
            default:
              return <Typography variant="body2">{page[key]}</Typography>;
          }
        }}
        ActionCell={ActionCell}
        actionCellWidth={80}
      />
      <ConfirmationDialog
        open={!!pageToDelete}
        onCancel={handleCloseDialog}
        onConfirm={handleConfirmDialog}
        title={i18n.t('pages.delete.title')}
      >
        <Typography variant="body1">
          {i18n.t('pages.delete.confirmation', {
            page: <b>{pageToDelete && pageToDelete.title}</b>,
          })}
        </Typography>
      </ConfirmationDialog>
      {pageToEdit && (
        <PageStateDialog page={pageToEdit} onClose={handleCloseEditDialog} />
      )}
    </MuiThemeProvider>
  ));
};

export default withStores('pagesStore')(PagesTable);
